import Icon from '@amzn/awsui-components-core/icon';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TestimonialEntity } from 'src/components/context/AppContextModels';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import quoteIconDark from 'src/styles/landing-page/testimonials-quote-dark.svg';
import quoteIconLight from 'src/styles/landing-page/testimonials-quote-light.svg';
import { SECTION_TITLES } from './landing-page-constants';
import { USER_BADGE_PHOTO_BASE_URL } from 'src/constants/AppConstants';

/** Parse summary into title (team name) and quote. Author info comes from users array. */
const parseTestimonialSummary = (summary: string): { title: string; quote: string; authorName: string; authorRole: string } | null => {
  // Match: [Title] "quote text" –optional attribution
  const match = summary.match(/^(\[.*?\])\s*["\u201c\u201f](.*?)["\u201d\u201e]\s*([\u2013\u2014\u2012\u2013-].*)?$/s);
  if (!match) return null;
  const attribution = match[3]?.trim().replace(/^[\u2013\u2014\u2012\u2013-]\s*/, '') || '';
  const [authorName, ...roleParts] = attribution.split(',').map((s) => s.trim());
  return {
    title: match[1].replace(/^\[|\]$/g, ''),
    quote: match[2].trim(),
    authorName: authorName || '',
    authorRole: roleParts.join(', ') || ''
  };
};

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * @feature landing-page
 * @owner das-finsuite-application-dev
 * @description Testimonials carousel — Version A progress bar that fills over 12s as a countdown.
 * When the bar completes, the card advances. Hover pauses the bar and resumes on mouse leave.
 */
const TestimonialsSection: React.FC = () => {
  const { testimonials: rawTestimonials } = useAppContext();
  const { isDarkMode } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);

  const testimonials = useMemo(() => shuffleArray(rawTestimonials).slice(0, 20), [rawTestimonials]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [testimonials]);

  // When the progress bar animation completes, advance to next card
  const handleProgressEnd = useCallback(() => {
    if (testimonials.length <= 1) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      setIsAnimating(false);
    }, 300);
  }, [testimonials.length]);

  // Restart progress bar animation when card changes
  useEffect(() => {
    if (progressRef.current) {
      const el = progressRef.current;
      el.style.animation = 'none';
      void el.offsetWidth; // force reflow
      el.style.animation = '';
    }
  }, [currentIndex]);

  const navigate = useCallback(
    (direction: 1 | -1) => {
      if (testimonials.length <= 1 || isAnimating) return;
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + direction + testimonials.length) % testimonials.length);
        setIsAnimating(false);
      }, 300);
    },
    [testimonials.length, isAnimating]
  );

  if (testimonials.length === 0) return null;

  const current: TestimonialEntity = testimonials[currentIndex];
  const parsed = parseTestimonialSummary(current.summary);
  const user = current.users?.[0];
  const login = user?.login;
  const photoUrl = login ? `${USER_BADGE_PHOTO_BASE_URL}${login}` : null;
  // Prefer users array for author info, fallback to parsed summary attribution
  const authorName = user ? `${user.first_name} ${user.last_name}` : parsed?.authorName || '';
  const authorRole = user?.business_title || parsed?.authorRole || '';
  const initials = authorName
    ? authorName
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : '?';

  return (
    <div className="landing-testimonials" data-testid="testimonials-section">
      <div className="landing-section__header">
        <h4>{SECTION_TITLES.testimonials}</h4>
      </div>

      <div
        className={`landing-testimonials__card${isAnimating ? ' landing-testimonials__card--fading' : ''}`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        data-testid="testimonials-card"
      >
        {/* Decorative gradient blobs */}
        <div className="landing-testimonials__blob landing-testimonials__blob--1" />
        <div className="landing-testimonials__blob landing-testimonials__blob--2" />

        {/* Quote icon */}
        <div className="landing-testimonials__quote-icon">
          <img src={isDarkMode ? quoteIconDark : quoteIconLight} alt="" width={40} height={40} />
        </div>

        {/* Content */}
        <div className="landing-testimonials__content">
          {parsed ? (
            <>
              <div className="landing-testimonials__pill" data-testid="testimonial-pill">
                {parsed.title}
              </div>
              <div className="landing-testimonials__quote" data-testid="testimonial-quote">
                {parsed.quote}
              </div>
              {authorName && (
                <div className="landing-testimonials__author" data-testid="testimonial-author">
                  <div className="landing-testimonials__avatar-glow" />
                  {photoUrl ? (
                    <img
                      className="landing-testimonials__avatar landing-testimonials__avatar--photo"
                      src={photoUrl}
                      alt={authorName}
                      data-testid="testimonial-avatar-photo"
                    />
                  ) : (
                    <div className="landing-testimonials__avatar" data-testid="testimonial-avatar-initials">
                      {initials}
                    </div>
                  )}
                  <div className="landing-testimonials__author-info">
                    <span className="landing-testimonials__author-name" data-testid="testimonial-author-name">
                      {authorName}
                    </span>
                    {authorRole && (
                      <span className="landing-testimonials__author-role" data-testid="testimonial-author-role">
                        {authorRole}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="landing-testimonials__quote" data-testid="testimonial-quote">
              {current.summary}
            </div>
          )}
        </div>

        {/* Navigation */}
        {testimonials.length > 1 && (
          <div className="landing-testimonials__nav">
            <button className="landing-testimonials__nav-btn" onClick={() => navigate(-1)} aria-label="Previous testimonial" type="button">
              <Icon name="angle-left" />
            </button>
            <button className="landing-testimonials__nav-btn" onClick={() => navigate(1)} aria-label="Next testimonial" type="button">
              <Icon name="angle-right" />
            </button>
          </div>
        )}

        {/* Progress bar — fills over time as countdown, pauses on hover */}
        <div className="landing-testimonials__progress" data-testid="testimonial-progress">
          <div
            ref={progressRef}
            className={`landing-testimonials__progress-fill${isPaused ? ' landing-testimonials__progress-fill--paused' : ''}`}
            onAnimationEnd={handleProgressEnd}
            data-testid="testimonial-progress-fill"
          />
        </div>
      </div>
    </div>
  );
};

export default TestimonialsSection;
